import React from 'react'
import Chart from 'react-apexcharts'

interface TrendData {
  date: string
  averageRating: number
  npsScore: number
  totalReviews: number
}

interface TrendChartProps {
  data: TrendData[]
  grouping: 'day' | 'month' | 'year'
}

export const TrendChart: React.FC<TrendChartProps> = ({data, grouping}) => {
  if (!data || data.length === 0) return null

  const formatLabel = (dateStr: string) => {
    if (grouping === 'year') return dateStr
    if (grouping === 'month') {
      const [year, month] = dateStr.split('-')
      return `${month}/${year}`
    }
    const [year, month, day] = dateStr.split('-')
    return `${day}/${month}/${year}`
  }

  const chartOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'line',
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      fontFamily: 'inherit',
    },
    stroke: {
      width: [0, 4, 4], // Barra (0px), Média (4px), NPS (4px)
      curve: 'smooth',
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '40%',
      },
    },
    xaxis: {
      categories: data.map((t) => formatLabel(t.date)),
      tooltip: {enabled: false},
      labels: {
        style: {colors: '#A1A5B7', fontSize: '12px', fontWeight: 600},
      },
    },
    yaxis: [
      {
        seriesName: 'Respostas (Qtd)',
        title: {
          text: 'Pesquisas Respondidas',
          style: {color: '#0d6efd', fontWeight: 600},
        },
        min: 0,
        decimalsInFloat: 0,
        labels: {
          style: {colors: '#A1A5B7', fontSize: '12px'},
        },
      },
      {
        opposite: true,
        seriesName: 'Avaliação Média',
        title: {
          text: 'Média (0 a 10)',
          style: {color: '#ffc107', fontWeight: 600},
        },
        min: 0,
        max: 10,
        labels: {
          style: {colors: '#A1A5B7', fontSize: '12px'},
        },
      },
      {
        show: false, // Escondemos o eixo NPS para não poluir
        seriesName: 'NPS',
        min: -100,
        max: 100,
      },
    ],
    colors: ['#0d6efd', '#ffc107', '#198754'],
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0], // Exibe números flutuando apenas na barra
      style: {colors: ['#FFF'], fontSize: '12px'},
      background: {
        enabled: true,
        foreColor: '#0d6efd',
        borderWidth: 0,
        // O TypeScript exige a declaração do dropShadow
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          color: '#000',
          opacity: 0.45,
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        // Tipado explicitamente para retornar string e lidar com undefined
        formatter: (y: number | undefined, {seriesIndex}: any): string => {
          if (typeof y !== 'undefined') {
            if (seriesIndex === 0) return `${y.toFixed(0)} respostas`
            if (seriesIndex === 1) return `${y.toFixed(1)} / 10`
            if (seriesIndex === 2) return y.toFixed(0)
            return String(y)
          }
          return ''
        },
      },
    },
    legend: {
      horizontalAlign: 'center',
      labels: {colors: '#7E8299'},
    },
  }

  const chartSeries = [
    {
      name: 'Total de Avaliações',
      type: 'column',
      data: data.map((t) => t.totalReviews),
    },
    {
      name: 'Avaliação Média',
      type: 'line',
      data: data.map((t) => Number(t.averageRating.toFixed(1))),
    },
    {
      name: 'NPS',
      type: 'line',
      data: data.map((t) => t.npsScore),
    },
  ]

  return (
    <div id='kt_charts_trend'>
      <Chart options={chartOptions} series={chartSeries} type='line' height={400} />
    </div>
  )
}

export default TrendChart
